import { Component, OnInit, OnDestroy } from '@angular/core';
import { Optional, Host, HostBinding } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subject, Subscription } from 'rxjs';

import { viewService } from '@unpispas/upp-base';

/**
 * Represents a tab bar component that contains multiple tab buttons.
 * It manages tab selection and emits an event when a tab is selected.
 */
@Component({
    selector: 'upp-tab-bar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-tabbar.html',
    styleUrl: './upp-tabbar.scss'
})
export class UppTabBarComponent implements OnDestroy {
    private _selected: string | null = null;
    private _selectionChanged = new Subject<void>();

    /** The currently selected tab name. */
    @Input()
    set selected(value: string | null){
        if (this._selected != value){
            this._selected = value;
            this._selectionChanged.next();
            this.change.markForCheck();
        }
    }

    get selected(): string | null {
        return this._selected;
    }

    @HostBinding('class.mobile')
    get IsMobile(): boolean {
        return this.view.Mobile === true;
    }

    /**
     * @constructor
     * @param change A reference to Angular's ChangeDetectorRef, used to manually control change detection.
     * @param view View-mode service used to publish the mobile host class.
     */    
    constructor(private change: ChangeDetectorRef, public view: viewService){
        // nothing to do
    }

    /**
     * Event emitted when a tab is selected.
     * The selected tab name is passed as a string.
     */    
    @Output() Selected = new EventEmitter <string> (); 

    /**
     * Observable that emits when selected input changes.
     */    
    get OnSelectionChanged(){
        return this._selectionChanged.asObservable();
    }

    /**
     * Handles tab selection by setting the current tab and emitting the selection event.
     * 
     * @param tabbutton - The name of the tab button that is selected.
     */    
    OnSelected(tabbutton: string){
        this.Selected.next(tabbutton);
        this.change.markForCheck();
    }

    /**
     * Gets the currently selected tab name.
     * 
     * @returns The name of the currently selected tab, or `null` if no tab is selected.
     */    
    get current(): string | null {
        return this._selected;
    }

    /**
     * Cleans subscriptions and internal subjects on destroy.
     */    
    ngOnDestroy(): void {
        this._selectionChanged.complete();
    }
}

/**
 * Represents a tab button that belongs to the `UppTabBarComponent`.
 * It communicates with the parent `UppTabBarComponent` when clicked.
 */
@Component({
    selector: 'upp-tab-button',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <ion-tab-button [ngClass]="{ 'selected': IsTab }" (click)="OnTab()">
            <ion-icon [name]="tabicon"></ion-icon>
            <ion-label>{{tablabel}}</ion-label>        
        </ion-tab-button>    
    `,
    styleUrl: './upp-tabbar.scss'
})
export class UppTabButtonComponent implements OnInit, OnDestroy {
    /** The name of the tab. */
    @Input() tabname: string | null = null;

    /** The label to be displayed inside the tab button. */
    @Input() tablabel: string | null = null;

    /** The icon name to be used for the tab button. */
    @Input() tabicon: string | null = null;

    @HostBinding('class.mobile')
    get IsMobile(): boolean {
        return this.view.Mobile === true;
    }

    /**
     * @constructor
     * @param change A reference to Angular's ChangeDetectorRef, used to manually control change detection.
     * @param view View-mode service used to publish the mobile host class.
     * @param tabbar The parent `UppTabBarComponent` instance (optional).
     */  
    constructor(
        private change: ChangeDetectorRef,
        public view: viewService,
        @Optional() @Host() private tabbar: UppTabBarComponent,
    ){
        // nothing to do
    }

    /** Subscription to the `Selected` event of the parent `UppTabBarComponent`. */
    private _tab_subscription: Subscription | null = null;

    /**
     * Lifecycle hook that is called after Angular initializes the component.
     * Subscribes to the `Selected` event of the parent `UppTabBarComponent`,
     * ensuring the tab updates when the selection changes.
     */    
    ngOnInit(){
        if (this.tabbar){
            this._tab_subscription = this.tabbar.OnSelectionChanged.subscribe(
            () => {
                this.change.markForCheck();
            })
        }
    }

    /**
     * Lifecycle hook that is called before Angular destroys the component.
     * Unsubscribes from the `Selected` event to prevent memory leaks.
     */    
    ngOnDestroy(){
        if (this._tab_subscription){
            this._tab_subscription.unsubscribe();
            this._tab_subscription = null;
        }
    }

    /**
     * Determines whether the tab is currently selected.
     * 
     * @returns `true` if this tab is selected, otherwise `false`.
     */    
    get IsTab(){
        if (this.tabbar && this.tabname) {
            return (this.tabbar.current == this.tabname);
        }

        return false;
    }

    /**
     * Handles the tab button click event.
     * Calls the `OnSelected` method of the parent `UppTabBarComponent` to notify of the tab selection.
     */    
    OnTab(){
        if (this.tabbar && this.tabname) {
            this.tabbar.OnSelected(this.tabname);
        }
    }
}